import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterAutosuggest } from '@components/toolbar/FilterAutosuggest'
import { FilterDatePicker } from '@components/toolbar/FilterDatePicker'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption } from '@models/Filter'

import { useLocalFilters } from '../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useOpdrachtenSearch'
import type { OpdrachtenSearchFilters as Filters } from '../types/OpdrachtenSearch'
import type { Werkbak } from '../types/Werkbak'

interface Props {
  onChange: (filters: Filters, removeSort?: boolean) => void
  onRefresh: () => void
  onReset: () => void
  filters: Filters
  initialFilters: Filters
  onSearchChange: any
  options: any
  werkbakken: Werkbak[]
  filterOptions: {
    gebieden: FilterOption[]
    aannemers: string[]
    netbeheerders: string[]
    versies: string[]
    disciplines: FilterOption[]
  }
}

export const OpdrachtenFilters = ({
  onChange,
  onRefresh,
  onReset,
  filters,
  initialFilters,
  onSearchChange,
  options,
  werkbakken,
  filterOptions: { gebieden, aannemers, netbeheerders, versies, disciplines },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    netbeheerder,
    aannemer,
    discipline,
    search,
    open,
    versieNummer,
    creatiedatum,
    changed,
    werkbak,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          options={werkbakken}
          label="Werkbak"
          filterKey="werkbak"
          value={werkbak}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={versies}
          label="Versienummer"
          filterKey="versieNummer"
          value={versieNummer}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={[{ code: null, label: 'Geen' }, ...gebieden] as any}
          value={gebied as any}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={disciplines}
          label="Discipline"
          filterKey="discipline"
          value={discipline}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterDatePicker
          label="Creatiedatum"
          filterKey="creatiedatum"
          value={creatiedatum}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterDatePicker
          label="Mutatiedatum"
          filterKey="changed"
          value={changed}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {/* If there is only 1 netbeheerder, hide the list. It's unnecessary to display it */}
      {netbeheerders.length > 1 && (
        <FilterGridItem>
          <FilterSelectString
            options={netbeheerders}
            label="Netbeheerder"
            filterKey="netbeheerder"
            value={netbeheerder}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      {aannemers.length > 1 && (
        <FilterGridItem>
          <FilterSelectString
            options={aannemers}
            label="Aannemer"
            filterKey="aannemer"
            value={aannemer}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <FilterAutosuggest
          search={search}
          options={options}
          onFilterChange={handleFilterChange}
          onSearchChange={onSearchChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Filters toepassen"
          onClick={handleZoeken}
        />

        <FilterToggleButton
          filterKey="open"
          value={open}
          onFilterChange={handleFilterChange}
          activeIcon={<AlarmRoundedIcon />}
          inactiveIcon={<AlarmOffRoundedIcon />}
          activeTitle="Klik om ook afgesloten opdrachten te tonen"
          inactiveTitle="Klik om alleen open opdrachten te tonen"
        />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
